import * as React from 'react';
import styled from 'styled-components';
import { ButtonMain } from '../atoms';

const PROJECT_TYPE_OPTIONS = [
  { value: '1_2_FAMILY', label: '1 & 2 Family Dwelling' },
  { value: 'MULTIFAMILY', label: 'Multifamily' },
  { value: 'COMMERCIAL', label: 'Commercial' },
];

const ProjectDetails = ({ initial, changeEditMode, hideButtons }: Props) => {
  const contactNames = initial.contacts?.length
    ? initial.contacts
        .map(el => {
          const fullName = [el?.firstName, el?.lastName].filter(Boolean).join(' ');
          return fullName || el?.companyName || el?.email || '';
        })
        .filter(Boolean)
        .join(', ')
    : 'No contacts';

  const projectType = initial.project_type || initial.projectType;
  const eboxConfirmationNumber = initial.eboxConfirmationNumber;

  return (
    <Wrapper>
      <TwoCol>
        <Col>
          <Field>
            <Label>Project Name</Label>
            <Value>{initial.name}</Value>
          </Field>
          <Field>
            <Label>Address</Label>
            <Value>{initial.address}</Value>
          </Field>
          <PairRow>
            <Field>
              <Label>City</Label>
              <Value>{initial.city}</Value>
            </Field>
            <Field>
              <Label>State</Label>
              <Value>{initial.state}</Value>
            </Field>
          </PairRow>
          <PairRow>
            <Field>
              <Label>Street</Label>
              <Value>{initial.street}</Value>
            </Field>
            <Field>
              <Label>Zip</Label>
              <Value>{initial.zipCode}</Value>
            </Field>
          </PairRow>
        </Col>
        <Col>
          <Field>
            <Label>Contacts</Label>
            <Value>{contactNames}</Value>
          </Field>
          <PairRow>
            <Field>
              <Label>Franchise</Label>
              <Value>{initial.franchiseName}</Value>
            </Field>
            <Field>
              <Label>Jurisdiction</Label>
              <Value>{initial.jurisdictionName}</Value>
            </Field>
          </PairRow>
          <Field>
            <Label>Project Manager</Label>
            <Value>{initial.managerName}</Value>
          </Field>
          <Field>
            <Label>Project Type</Label>
            <TypePills>
              {PROJECT_TYPE_OPTIONS.map(option => (
                <TypePill key={option.value} $active={projectType === option.value}>
                  <PillDot $active={projectType === option.value} />
                  {option.label}
                </TypePill>
              ))}
            </TypePills>
          </Field>
        </Col>
      </TwoCol>
      <EboxBlock>
        <SectionTitle>Ebox</SectionTitle>
        <Field>
          <Label>Ebox Number</Label>
          {eboxConfirmationNumber ? <Value>{eboxConfirmationNumber}</Value> : <NotAssigned>Ebox not Assigned</NotAssigned>}
        </Field>
      </EboxBlock>
      {!hideButtons && (
        <ButtonRow>
          <EditButton variant="contained" color="primary" onClick={() => changeEditMode(true)}>
            Edit
          </EditButton>
        </ButtonRow>
      )}
    </Wrapper>
  );
};

interface Props {
  initial: any;
  changeEditMode: (data: boolean) => void;
  hideButtons?: boolean;
}

const Wrapper = styled.div`
  width: 100%;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0 var(--space-6);
  align-items: start;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const PairRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-2) 0;
`;

const Label = styled.div`
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  color: var(--neutral-800);
  line-height: 21px;
`;

const Value = styled.div`
  font-size: var(--fs-base);
  color: var(--neutral-800);
  padding: 0 12px;
  background: var(--neutral-0);
  border: 1px solid var(--neutral-300);
  border-radius: var(--radius-md);
  height: 40px;
  display: flex;
  align-items: center;
`;

const NotAssigned = styled.div`
  font-size: var(--fs-base);
  color: var(--neutral-500);
  font-style: italic;
  padding: 0 12px;
  background: var(--neutral-50);
  border: 1px dashed var(--neutral-300);
  border-radius: var(--radius-md);
  height: 40px;
  display: flex;
  align-items: center;
`;

const EboxBlock = styled.div`
  margin-top: var(--space-6);
  padding-top: var(--space-5);
  border-top: 1px solid var(--neutral-200);
`;

const SectionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: var(--fw-semibold);
  color: var(--neutral-800);
  margin-bottom: var(--space-3);
`;

const TypePills = styled.div`
  display: flex;
  gap: var(--space-2);
  flex-wrap: nowrap;
  padding-top: var(--space-1);
`;

const TypePill = styled.div<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary-600)' : 'var(--neutral-200)')};
  background: ${({ $active }) => ($active ? 'var(--primary-50)' : 'var(--neutral-0)')};
  color: ${({ $active }) => ($active ? 'var(--primary-700)' : 'var(--neutral-500)')};
  font-size: var(--fs-sm);
  font-weight: ${({ $active }) => ($active ? 'var(--fw-semibold)' : 'var(--fw-regular)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex-shrink: 1;
`;

const PillDot = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: ${({ $active }) => ($active ? 'var(--primary-600)' : 'var(--neutral-300)')};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: var(--space-6);
`;

const EditButton = styled(ButtonMain)`
  min-width: 160px;
  min-height: 44px;
  font-size: var(--fs-lg);
`;

export default ProjectDetails;
